/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";

import React, { Component, FC, useCallback, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { Store, useSelector } from "../../store";

import firebase from "firebase/app";
import "firebase/firestore";

import "firebase/auth";
import { Timestamp } from "@google-cloud/firestore";
import { purchaseActions } from "../../modules/purchase";
import { modalActions, ReqToggleError } from "../../modules/modal";
import {
  streamingActions,
  ReqFetchGiftItems,
  ReqSetStateByKey,
  GiftItemData,
  StreamingType,
  ReqFetchShardIdArgs,
} from "../../modules/streaming";
import { ticketActions } from "../../modules/ticket";

import { errorReport } from "../../utility/logger";
import { fetchFirestoreDocument, setFirestoreDocument } from "utility/firebase";
import {
  getEventIdFromHref,
  domain_from_url,
  getCookieValue,
  set_cookie,
  EventInfo,
  scrollTop,
  isSmartPhone,
} from "../../utility";
import {
  loadCurrentTimeFromSessionStorage,
  saveCurrentTimeToSessionStorage,
} from "../../utility/streaming";

import appConfig from "../../constants/appConfig";
import { enableNpaw } from "./enableNpaw";
import { PCView } from "components/common/MediaQuery";
import { v4 as uuidv4 } from "uuid";
import { withTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { AlertBar } from "components/atoms/AlertBar";
import {
  isPlayerSupported,
  PlayerEventType,
  PlayerState,
} from "amazon-ivs-player";
// @ts-expect-error TS7016
import youbora from "youboralib";
import "youbora-adapter-html5";
import type { Cookie } from "@spwn/types/functions";
import {
  EventVideo,
  THEOSetting,
  FingerprintArray,
} from "@spwn/types/firebase/firestore";
import { checkIfUseCloudFlare } from "tmp/canaryRelease/cloudflareVOD";
import StreamingScreenV2 from "./StreamingScreenV2";
import {
  createIVSVideoPlayer,
  createPlainVideoPlayer,
  IVSVideoPlayer,
  PlainVideoPlayer,
} from "./videojsPlayer";
import type { ResolvedTHEOPlayerRuntime } from "./videojsComponent/theoplayerRuntime";
import { Box } from "@material-ui/core";
import StreamingActionAreaV2 from "./StreamingActionAreaV2";
import screenfull from "screenfull";
import { isFeatureEnabled } from "utility/isFeatureEnabled";
import { DUMMY_HLS_INDEX_URL } from "constants/streaming";
import { StreamingFullscreen } from "./StreamingFullscreen";
import useActionAreaType from "./useActionAreaType";
import useCommentFilter from "./useCommentFilter";
import { useFullscreen } from "./useFullscreen";

import StreamingBottomArea from "./StreamingBottomArea";
import { isPopOut } from "./enableCommentPopOut";
import { useAnnouncement } from "./useAnnouncement";
import { fullscreenClassName } from "./videojsComponent/videojsFullScreenModeButton";
import {
  TheaterModeContext,
  useTheaterMode,
} from "contexts/TheaterModeContext";
import { useLiveEvent } from "./useLiveEvent";
import { toUrlSafeForSignedUrl } from "utility/cloudfront";
import { useIsChatAdmin } from "./useIsChatAdmin";
import PawCharge from "containers/modal/pawCharge/PawCharge";

type Props = ValueProps &
  FuncProps & {
    theoPlayerRuntime: ResolvedTHEOPlayerRuntime;
    url: string;
    event: EventInfo;
    hasTicket: boolean;
    cookies: {
      [videoId: string]: {
        LL?: Cookie;
        default: Cookie;
        LL_HLS?: Cookie;
      };
    };
    cookiesList?: {
      [videoId: string]: {
        [videoId: string]: {
          LL?: Cookie;
          default: Cookie;
          LL_HLS?: Cookie;
        };
      }[];
    } | null;
    vid: string;
    vodSetting: THEOSetting;
    liveSetting: THEOSetting;
    eventVideoMap: { [videoId: string]: EventVideo };
    t: TFunction;
    getStreamingKey?: () => Promise<void>;
    isFanClub?: boolean;
    isLLHLSEnabled: boolean;
    isMMSForVODEnabled: boolean;
    isSignedUrlEnabled: boolean;
    isPollingEnabled: boolean;
    getVideoGroupsSet: (
      currentVideoId: string
    ) => { mainVideoId: string; syncPlaybackTimeOnSwitch: boolean } | undefined;
  };

interface ValueProps {
  pathname: string;
  giftItemMap: { [key: string]: GiftItemData };
  pawBalance: number | undefined;
  isDarkModeEnabled: boolean;
  isOnlyChatEnabled: boolean;
  isOpenError: boolean;
  currentVideoId: string | null;
}

interface FuncProps {
  getEmoBalance: () => void;
  fetchGiftItems: (data: ReqFetchGiftItems) => void;
  // @ts-expect-error TS7051
  usePAW: (any) => void;
  toggleError: (any: ReqToggleError) => void;
  setStateByKey: (payload: ReqSetStateByKey) => void;
  fetchCommentShardId: (payload: ReqFetchShardIdArgs) => void;
  setCurrentVideoId: (videoId: string) => void;
}

interface States {
  theaterModeState: boolean;
  firstMutedFlag: boolean;
  use_ll: boolean;
  hasURL: boolean; // if false, delete touch sensor
  hasURLUpdatingRequested: boolean;
  isDarkModeEnabled: boolean;
  eventStart: Timestamp;
  isSuperChat: boolean;
  THEOPlayerElementHeight: number;
  exceedWindowLimit: boolean;
  isControllerRendering: boolean;
  isCancelLowLatencyMessage: boolean;
  isControllerDisplaying: boolean;
}

const isError = (error: unknown): error is Error => error instanceof Error;

/**
 * 対象の配信が低遅延モードが使用可能か
 */
const hasLowLatency = (
  cookies: {
    [videoId: string]: {
      LL?: Cookie;
      default: Cookie;
    };
  },
  currentVideoId: string
): boolean => {
  return (
    cookies &&
    cookies.hasOwnProperty(currentVideoId) &&
    // @ts-expect-error TS2532
    cookies[currentVideoId].hasOwnProperty("LL")
  );
};

/**
 * 対象videoのstremingTypeを取得する
 * - streamingType
 *   - Live ライブ配信
 *   - VOD アーカイブ配信
 *
 * NOTE: videoのhasVODは後発で追加されたため、undefinedのチェックを行なっている
 * 優先度: event.hasVOD > video.hasVOD
 */
const getVideoStreamingType = (
  video: EventVideo,
  event: EventInfo
): StreamingType => {
  const hasVODOfVideo = video.hasVOD;
  return hasVODOfVideo === undefined
    ? event.hasVOD
      ? "VOD"
      : "Live"
    : event.hasVOD && hasVODOfVideo
      ? "VOD"
      : "Live";
};

class Streaming extends Component<Props, States> {
  static contextType = TheaterModeContext;

  _eventId = null;
  _player: PlainVideoPlayer | null = null;
  _videoRef: HTMLVideoElement | null = null;
  _IVSVideoRef: HTMLVideoElement | null = null;
  _aws_player: IVSVideoPlayer | null = null;
  _aws_youbora = null;
  _is_initial_play = true; // to explicit muted auto play
  _streamingUrl = null;
  _streamingType: StreamingType = "Live";
  _fingerprint = String(uuidv4());
  _unsubscribe = null;
  _fullscreenElementRef = React.createRef<HTMLDivElement>();
  _hoverObserver: MutationObserver | null = null;
  _hasLL = false;
  _onLLHLSError: ((error: unknown) => void) | null = null;
  _isFirstSetWatchVideo = true;
  // 登録済み interceptor を保持して差し替え/解除に使う
  _requestInterceptor:
    | ((request: THEOplayer.InterceptableRequest) => void)
    | null = null;

  // @ts-expect-error TS7006
  constructor(props) {
    super(props);
    this.setStreamingType(this.props.vid);
    this.props.getEmoBalance();
    // @ts-expect-error TS2322
    this.props.fetchGiftItems({ eid: this.props.event._id });
    // @ts-expect-error TS2322
    this._eventId = getEventIdFromHref(this.props.pathname, "events");
    const video = this.props.eventVideoMap[this.props.vid];
    // @ts-expect-error TS2345
    const streamingType = getVideoStreamingType(video, this.props.event);
    this._hasLL =
      isPlayerSupported &&
      streamingType === "Live" &&
      hasLowLatency(this.props.cookies, this.props.vid);

    /**
     * 低遅延モードが使用可能な場合、デフォルトで低遅延モードを使用するかのフラグ
     * MMSを使用するiOS端末の場合、低遅延モードはHLSのLL-HLSで提供される
     * MMSの挙動が不安定なため、LLHLSを使用するios端末では、デフォルトで低遅延モードを使用しない
     */
    const use_ll = this.canUseLLHLS(streamingType, this.props.vid)
      ? false
      : this._hasLL;

    this.state = {
      theaterModeState: false,
      firstMutedFlag: false,
      // 低遅延モードが使用可能であればデフォルトを低遅延モードにする
      use_ll,
      hasURL: true,
      hasURLUpdatingRequested: true,
      isDarkModeEnabled: false,
      // @ts-expect-error TS2322
      eventStart: null,
      isSuperChat: false,
      // @ts-expect-error TS2322
      THEOPlayerElementHeight: null,
      exceedWindowLimit: false,
      isControllerRendering: false,
      isCancelLowLatencyMessage: false,
      isControllerDisplaying: false,
    };

    // this.check_geo(this._eventId)

    this.props.setCurrentVideoId(this.props.vid);
  }

  get player() {
    if (this._player) {
      return this._player;
    } else {
      throw new Error("playerが初期化されていません。");
    }
  }

  get IVSPlayer() {
    if (this._aws_player) {
      return this._aws_player;
    } else {
      throw new Error("IVS playerが初期化されていません。");
    }
  }

  canUseLLHLS(streamingType: StreamingType, videoId: string) {
    return (
      this.canUseMMS() &&
      streamingType === "Live" &&
      this.props.cookies?.[videoId]?.LL_HLS !== undefined &&
      this.props.isLLHLSEnabled
    );
  }

  canUseMMS(): boolean {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const canUseMMS = "ManagedMediaSource" in window;
    return isIOS && canUseMMS;
  }

  fetchCommentShardId() {
    this.props.fetchCommentShardId({
      chatVersion:
        this.props.event.streamingInfo?.optionSettings?.chatVersion || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
      nCommentShards:
        this.props.event.streamingInfo?.optionSettings?.nCommentShards,
      isCommentShardingEnabled:
        this.props.event.streamingInfo?.optionSettings
          ?.isCommentShardingEnabled ?? false,
      // @ts-expect-error TS2322
      userId: firebase.auth()?.currentUser?.uid || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
      // @ts-expect-error TS2322
      eventId: this.props.event._id,
      videoId: this.props.vid,
    });
  }

  getDvrStartTime(videoId: string): number | undefined {
    const video = this.props.eventVideoMap?.[videoId];
    const dvrStartTime =
      video && video.dvrStartTime
        ? Math.floor(video.dvrStartTime.toMillis() / 1000)
        : undefined;

    return dvrStartTime;
  }

  componentDidMount() {
    const { isTheaterModeEnabled, isForceTheaterMode, toggleTheaterMode } =
      this.context;

    if (isPopOut()) {
      this.fetchCommentShardId();
      return;
    }

    const useLLHLS = this.canUseLLHLS(this._streamingType, this.props.vid);

    if (this._streamingType === "VOD") {
      this._player = createPlainVideoPlayer({
        theoPlayerRuntime: this.props.theoPlayerRuntime,
        setting: this.props.vodSetting,
        fullscreenElementRef: this._fullscreenElementRef,
        streamingType: this._streamingType,
        videoId: this.props.vid,
        hasLL: this._hasLL,
        useLLHLS,
        useLL: this.state.use_ll,
        isTheaterModeEnabled,
        isForceTheaterMode,
        toggleTheaterMode,
        toggleLowLatency: this.toggleLowLatency,
        handleSwitchMultiAngleVideo: this.handleSwitchMultiAngleVideo,
      });
    } else {
      this._player = createPlainVideoPlayer({
        theoPlayerRuntime: this.props.theoPlayerRuntime,
        setting: this.props.liveSetting,
        fullscreenElementRef: this._fullscreenElementRef,
        streamingType: this._streamingType,
        videoId: this.props.vid,
        hasLL: this._hasLL,
        useLLHLS,
        useLL: this.state.use_ll,
        isTheaterModeEnabled,
        isForceTheaterMode,
        toggleTheaterMode,
        toggleLowLatency: this.toggleLowLatency,
        handleSwitchMultiAngleVideo: this.handleSwitchMultiAngleVideo,
      });
    }

    // initialize device unique id
    // @ts-expect-error TS2322
    this._fingerprint = localStorage.getItem("youbora.youboraDeviceUUID");
    if (!this._fingerprint) {
      // @ts-expect-error TS2322
      this._fingerprint = sessionStorage.getItem("fingerprint");
    }
    if (!this._fingerprint) {
      this._fingerprint = String(uuidv4());
    }
    sessionStorage.setItem("fingerprint", this._fingerprint);

    this.setFingerprint();

    if (!this._IVSVideoRef) {
      throw new Error("IVSVideoRef が null です。");
    }

    this._aws_player = createIVSVideoPlayer({
      id: this._IVSVideoRef,
      fullscreenElementRef: this._fullscreenElementRef,
      isTheaterModeEnabled,
      isForceTheaterMode,
      hasLL: this._hasLL,
      useLL: this.state.use_ll,
      toggleTheaterMode,
      toggleLowLatency: this.toggleLowLatency,
      handleSwitchMultiAngleVideo: this.handleSwitchMultiAngleVideo,
    });

    // eslint-disable-next-line eqeqeq
    if (getCookieValue("preferDarkMode") == "true") {
      // !! 文字列なのかtrueなのかはっきりさせて確実に評価する
      this.setState({ isDarkModeEnabled: true });
    }

    document.addEventListener("keydown", this.handleKeyDown);
    this.player.addEventListener("timeupdate", this.handleTimeUpdate);

    this.setState({
      // @ts-expect-error TS2322
      THEOPlayerElementHeight: document.getElementById("video")?.clientHeight,
    });
    window.addEventListener("resize", () =>
      this.setState({
        // @ts-expect-error TS2322
        THEOPlayerElementHeight: document.getElementById("video")?.clientHeight,
      })
    );
    this.fetchCommentShardId();
  }

  private _isSwitchingVideo = false;
  handleSwitchMultiAngleVideo = async (videoId: string) => {
    if (this._isSwitchingVideo) return;
    this._isSwitchingVideo = true;
    try {
      await this.setWatchVideo({ vid: videoId, skipGetStreamingKey: true });
    } catch (e) {
      console.error(e);
    } finally {
      this._isSwitchingVideo = false;
    }
  };

  componentDidUpdate(prevProps: Props) {
    if (isPopOut()) {
      return;
    }
    // when videoId changed and the video stream url is exist, update current video.
    if (
      this.state.hasURLUpdatingRequested &&
      this.props.currentVideoId &&
      this.props.cookies?.[this.props.currentVideoId]
    ) {
      this.setWatchVideo({
        vid: this.props.currentVideoId,
        prevVid: prevProps.currentVideoId ?? undefined,
      });
      this.setState({ hasURLUpdatingRequested: false });
    }
    // when parent videoId is changed, update current video.
    if (this.props.vid !== prevProps.vid) {
      this.props.setCurrentVideoId(this.props.vid);
      this.setState({ hasURLUpdatingRequested: true });
    }
  }

  /**
   * apply streaming type by hasVOD flag. priority: event.hasVOD > video.hasVOD
   * @param vid
   */
  setStreamingType = (vid: Props["vid"]) => {
    const video = this.props.eventVideoMap[vid];
    // @ts-expect-error TS2345
    const streamingType = getVideoStreamingType(video, this.props.event);
    this._streamingType = streamingType;
    this.props.setStateByKey({ streamingType });
  };

  // event listener に async function を渡すのは非推奨であることと await による記述を両立するため即時関数を使用
  setFingerprint = () => {
    (async () => {
      try {
        const userId = firebase.auth()?.currentUser?.uid;
        const eventId = this._eventId;
        if (!userId || !eventId) return;
        const registeredFingerprints =
          await fetchFirestoreDocument<FingerprintArray>(
            `/users/${userId}/playingState/${eventId}`
          );
        let newFingerprints = [];
        const now = new Date();
        if (registeredFingerprints) {
          const { fingerprints } = registeredFingerprints;
          //fingerprint field is null
          if (!fingerprints) {
            newFingerprints.push({
              fingerprint: this._fingerprint,
              createdAt: now,
            });
          }
          //my fingerprint is not set
          else if (
            !fingerprints.some(
              (fingerprint) => fingerprint.fingerprint === this._fingerprint
            )
          ) {
            newFingerprints = fingerprints.sort(
              (fingerprint1, fingerprint2) => {
                return (
                  // @ts-expect-error TS2551
                  fingerprint2["createdAt"].seconds -
                  // @ts-expect-error TS2551
                  fingerprint1["createdAt"].seconds
                );
              }
            );
            //pop old fingerprint
            if (newFingerprints.length > 0) {
              if (
                this.props.event?.nWindowLimit &&
                this.props.event.nWindowLimit > 0
              ) {
                const { nWindowLimit } = this.props.event;
                if (newFingerprints.length > nWindowLimit - 1) {
                  const n = Math.min(nWindowLimit - 1, newFingerprints.length);
                  newFingerprints = newFingerprints.slice(0, n);
                }
              } else {
                newFingerprints = [newFingerprints[0]];
              }
            }
            //add my finger print
            newFingerprints.push({
              fingerprint: this._fingerprint,
              // @ts-expect-error TS2345
              createdAt: now,
            });
            //keep current fingerprints (my fingerprint already has been set)
          } else {
            newFingerprints = fingerprints;
            //reject old finger print if fingerprints length is greater than nWindowLimit
            if (
              this.props.event?.nWindowLimit &&
              this.props.event.nWindowLimit > 0
            ) {
              const { nWindowLimit } = this.props.event;
              if (newFingerprints.length > nWindowLimit) {
                newFingerprints = fingerprints.sort(
                  (fingerprint1, fingerprint2) => {
                    return (
                      // @ts-expect-error TS2551
                      fingerprint2["createdAt"].seconds -
                      // @ts-expect-error TS2551
                      fingerprint1["createdAt"].seconds
                    );
                  }
                );
                const n = Math.min(nWindowLimit, newFingerprints.length);
                newFingerprints = newFingerprints.slice(0, n);
              }
            }
          }
          // create first fingerprint
        } else {
          newFingerprints.push({
            fingerprint: this._fingerprint,
            createdAt: now,
          });
        }
        await setFirestoreDocument(
          `/users/${userId}/playingState/${eventId}`,
          {
            fingerprints: newFingerprints,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          false
        );
        if (this._aws_player) {
          this._aws_player.removeEventListener(
            PlayerState.PLAYING,
            this.setFingerprint
          );
        }
        if (!this._unsubscribe) this.watchPlayingState();
      } catch (e) {
        console.error(e);
      }
    })();
  };

  watchPlayingState = () => {
    const userId = firebase.auth()?.currentUser?.uid;
    const eventId = this._eventId;
    if (!userId || !eventId || this._unsubscribe) return;
    // @ts-expect-error TS2322
    this._unsubscribe = firebase
      .firestore()
      .doc(`/users/${userId}/playingState/${eventId}`)
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.data();
          // @ts-expect-error TS2339
          const { fingerprints } = data;
          if (
            !!fingerprints &&
            !fingerprints.some(
              // @ts-expect-error TS7006
              (fingerprint) => fingerprint.fingerprint === this._fingerprint
            ) &&
            this._player &&
            this.props.event &&
            this.props.event?.enableWindowLimit
          ) {
            this.setState({ exceedWindowLimit: true });
            if (!this.props.isOpenError) {
              this.props.toggleError({
                msg: "他の端末での再生が検知されたため再生を停止しました<br/>Too many people are using your account right now",
              });
            }
            this._player.pause();
            this._player.addEventListener("play", this.setFingerprint);
            if (this._aws_player) {
              this._aws_player.pause();
              this._aws_player.addEventListener(
                PlayerState.PLAYING,
                this.setFingerprint
              );
            }
          }
          console.log("playingSate", this.state.exceedWindowLimit);
        },
        (error) => {
          console.error(error);
        }
      );
  };

  // @ts-expect-error TS7006
  getRandomInt = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  setLL = (checked: boolean) => {
    this.setState({
      use_ll: checked,
      hasURLUpdatingRequested: true,
    });
  };

  toggleLowLatency = () => this.setLL(!this.state.use_ll);

  /**
   * videoを切り替える
   *
   * 呼ばれるトリガー
   * - 配信ページに入る
   * - EventVideoListで配信を切り替える
   * - 低遅延モードを切り替える
   */
  setWatchVideo = async ({
    vid,
    prevVid,
    skipGetStreamingKey,
  }: {
    vid: string;
    prevVid?: string;
    skipGetStreamingKey?: boolean;
  }) => {
    const isFirstCall = this._isFirstSetWatchVideo;
    this._isFirstSetWatchVideo = false;

    if (
      !isFirstCall &&
      vid !== prevVid &&
      this.props.getStreamingKey !== undefined &&
      skipGetStreamingKey !== true
    ) {
      await this.props.getStreamingKey();
    }
    // update url when user has some cookies (streamig has started or user is admin)
    if (this.props.cookies !== null && Boolean(this.props.cookies[vid])) {
      // check if url is present. if url is not present, set video poster.

      // 低遅延モードが使用可能か
      const video = this.props.eventVideoMap[vid];
      // @ts-expect-error TS2345
      const streamingType = getVideoStreamingType(video, this.props.event);

      this._hasLL =
        isPlayerSupported &&
        streamingType === "Live" &&
        hasLowLatency(this.props.cookies, vid);
      const useLL = this.state.use_ll && this._hasLL;

      const useLLHLS =
        this.state.use_ll && this.canUseLLHLS(streamingType, vid);

      const hasURL = (() => {
        if (useLLHLS) {
          return Boolean(this.props.cookies[vid]?.LL_HLS?.url);
        }

        return useLL
          ? Boolean(this.props.cookies[vid]?.LL?.url)
          : Boolean(this.props.cookies[vid]?.default?.url);
      })();

      this.setState({ hasURL });

      if (useLLHLS && hasURL) {
        const { cookies, cookiesList } = this.props;
        let settingCookie = cookies?.[vid]?.["LL_HLS"];
        const cookie = cookiesList?.[vid];
        if (cookie) {
          const idx = this.getRandomInt(0, cookie.length - 1);
          const selectedCookie = cookie[idx]?.[vid];
          settingCookie = selectedCookie?.["LL_HLS"];
        }
        if (settingCookie) {
          this.updatePlayer({
            cookie: settingCookie,
            vid,
            streamingType,
            hasLLToggle: this._hasLL,
            useIVS: false,
            useLLHLS: true,
          });
        }
      } else if (hasURL) {
        const { cookies, cookiesList } = this.props;
        let settingCookie = useLL
          ? cookies?.[vid]?.["LL"]
          : cookies?.[vid]?.["default"];
        const cookie = cookiesList?.[vid];
        if (cookie) {
          let idx = 0;
          if (streamingType === "Live") {
            // liveの場合はランダムに選択する
            // VODの場合はindex=0を選択し、vod2にリクエストが行かないようにする
            idx = this.getRandomInt(0, cookie.length - 1);
          }
          const selectedCookie = cookie[idx]?.[vid];
          settingCookie = useLL
            ? selectedCookie?.["LL"]
            : selectedCookie?.["default"];
        }
        if (settingCookie) {
          // dummyHLSが指定されている場合はサーバー起動しなくて良いようにテスト用のURLに差し替え
          if (
            appConfig.isProd !== "true" &&
            isFeatureEnabled({ featureName: "dummyHLS" })
          ) {
            settingCookie.url = DUMMY_HLS_INDEX_URL;
            settingCookie.cors = false;
            // dummyHLSの場合はIVSを確認する用にstateのuse_llのみで切り替えを行う
            this.updatePlayer({
              cookie: settingCookie,
              vid,
              streamingType,
              hasLLToggle: this._hasLL,
              useIVS: this.state.use_ll,
            });
          } else {
            this.updatePlayer({
              cookie: settingCookie,
              vid,
              streamingType,
              hasLLToggle: this._hasLL,
              useIVS: useLL,
            });
          }
        }
      } else {
        this.hideAWSPlayer();
        // make source empty to stop streaming
        this.player.src(undefined);
        // just show up THEOPlayer when source url is empty
        const theo_video_elem = document.querySelector(".theoplayer-container");
        theo_video_elem?.setAttribute("style", "display: 'block'");
        const touch_area_wrap = document.querySelector("#touch-area-wrap");
        touch_area_wrap?.setAttribute("style", "display: 'block'");

        // use video thumbnail for poster
        this._player?.poster(
          this.props.eventVideoMap[vid]?.thumbnail || this.props.event.thumbnail // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
        );
      }
    }
    // @ts-expect-error TS2322
    this._streamingUrl = this.props.url;
    // set event start time
    if (this.props.eventVideoMap) {
      const video = this.props.eventVideoMap[vid];
      if (video && video.chatDatetime) {
        this.setState({ eventStart: video.chatDatetime });
      } else {
        // @ts-expect-error TS2322
        this.setState({ eventStart: this.props.event.chatDatetime });
      }
    }

    // リロードで配信ページに入った場合に表示するビデオを決定するため、ビデオ切り替え時に閲覧中ビデオidをqueryに持たせる
    const url = new URL(window.location.href);
    url.searchParams.set("vid", vid);
    window.history.pushState({}, "", url);

    this.setStreamingType(vid);

    // ReduxにcurrentVideoIdを保存
    this.props.setCurrentVideoId(vid);
  };

  _keyCodes = {
    space: 32,
    right_arrow: 39,
    left_arrow: 37,
  };

  handleTimeUpdate = () => {
    // liveの場合は再生時間を保存しない
    if (this._streamingType === "Live") {
      return;
    }

    const { currentTime } = this.player;
    if (
      currentTime !== null &&
      currentTime !== undefined &&
      this.props.currentVideoId
    ) {
      const videoGroupsSet = this.props.getVideoGroupsSet(
        this.props.currentVideoId
      );

      if (videoGroupsSet) {
        const videoId = videoGroupsSet.syncPlaybackTimeOnSwitch
          ? videoGroupsSet.mainVideoId
          : this.props.currentVideoId;
        saveCurrentTimeToSessionStorage(videoId, currentTime.toString());
      }
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (
      !this._player ||
      !this._player.currentSrc() ||
      this._streamingType === "Live"
    ) {
      return;
    }
    if (this._player.seekable().length === 0) {
      return;
    }
    const begin = this._player.seekable().start(0);
    const end = this._player.seekable().end(0);
    const current = this._player.currentTime;
    if (current === undefined || begin === undefined || end === undefined) {
      return;
    }

    switch (event.keyCode) {
      case this._keyCodes.space:
        event.preventDefault();
        if (this._player.paused()) {
          this._player.play();
        } else {
          this._player.pause();
        }
        break;
      case this._keyCodes.right_arrow:
        if (current + 5 < end) {
          this._player.setCurrentTime(current + 5);
        }
        break;
      case this._keyCodes.left_arrow:
        if (current - 5 >= begin) {
          this._player.setCurrentTime(current - 5);
        } else {
          this._player.setCurrentTime(begin);
        }
        break;
      default:
        break;
    }
  };

  componentWillUnmount() {
    // ストリーミングページのみで使用する className を削除する
    // document.getElementById(appConfig.indexRoot).removeAttribute("class");
    // document.getElementById("container").removeAttribute("style");

    document.removeEventListener("keydown", this.handleKeyDown);

    try {
      if (this._player) {
        this._player.removeEventListener("timeupdate", this.handleTimeUpdate);
      }
    } catch (e) {
      console.error(e);
    }

    window.removeEventListener("resize", () =>
      this.setState({
        // @ts-expect-error TS2322
        THEOPlayerElementHeight: document.getElementById("video")?.clientHeight,
      })
    );

    if (this._unsubscribe) {
      // @ts-expect-error TS2349
      this._unsubscribe();
    }

    if (this._onLLHLSError && this._player) {
      try {
        this._player.removeEventListener("error", this._onLLHLSError);
      } catch {
        // playerの状態によってはremoveEventListenerが失敗する可能性があるため無視
      }
      this._onLLHLSError = null;
    }

    try {
      if (this._player) {
        this._player.destroy();
      }
    } catch (e) {
      console.error(e);
      // @ts-expect-error TS2345
      errorReport(isError(e) && e);
    }

    try {
      if (this._aws_player) {
        this._aws_player.destroy();
      }
    } catch (e) {
      console.error(e);
      // @ts-expect-error TS2345
      errorReport(isError(e) && e);
    }

    try {
      if (this._aws_youbora) {
        // @ts-expect-error TS2339
        this._aws_youbora.removeAdapter();
      }
    } catch (e) {
      console.error(e);
      // @ts-expect-error TS2345
      errorReport(isError(e) && e);
    }

    try {
      if (this._requestInterceptor) {
        this.player.network.removeRequestInterceptor(this._requestInterceptor);
        this._requestInterceptor = null;
      }
    } catch (e) {
      console.error(e);
    }

    if (this._hoverObserver) {
      this._hoverObserver.disconnect();
      this._hoverObserver = null;
    }
  }

  cancelLLPlaying = (_e = null) => {
    // LL機能がキャンセルした時,stateにtrueを格納する
    // 再度LLを有効にする可能性がある為、有効にした時の処理でstateにfalseを入れる
    this.setState({
      use_ll: false,
      hasURLUpdatingRequested: true,
      isCancelLowLatencyMessage: true,
    });
    window.setTimeout(() => {
      this.setState({ isCancelLowLatencyMessage: false });
    }, 5000);
  };

  /**
   * 低遅延Player（IVS Player）への切り替えのため、THEOPlayerを無効にする
   *
   * NOTE: IVS Player への切り替え以外で利用しないこと
   */
  hideTHEOPlayer = () => {
    if (this._player) {
      this.player.setMuted(true);
      /**
       * this._player.pause() だと、今読み込んでいる配信リソース（source）はリリースしないので、裏で読み込み続けることになる。
       * 低遅延モードへの切り替えは、player自体の切り替えで、sourceを読み込み続ける必要はないのでstopにすることでsourceをリリースする。
       * stopはdestroyと違って、player自体はリリースされないので、再度sourceを読み込み直せば再生可能である
       * ここでは activateTHEOPlayer によってsourceの再読み込みが実行されるのでstopとしている
       */
      this.player.pause();
      this.player.src(undefined);

      const videoElement = this.player.getHTMLVideoElement();
      videoElement.style.display = "none";

      if (this._onLLHLSError) {
        this.player.removeEventListener("error", this._onLLHLSError);
        this._onLLHLSError = null;
      }

      try {
        if (this._requestInterceptor) {
          this.player.network.removeRequestInterceptor(
            this._requestInterceptor
          );
          this._requestInterceptor = null;
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  hideAWSPlayer = () => {
    if (this._aws_player) {
      this._aws_player.setMuted(true);
      this._aws_player.setVolume(0.0); // add this because some times set muted not worked correctly...
      this._aws_player.pause();

      const element = this._aws_player.getHTMLVideoElement();
      element.style.display = "none";
      this._aws_player.removeIVSPlayerEventListener(
        PlayerState.ENDED,
        // @ts-expect-error TS2345
        this.cancelLLPlaying
      );
      this._aws_player.removeIVSPlayerEventListener(
        PlayerEventType.ERROR,
        // @ts-expect-error TS2345
        this.cancelLLPlaying
      );
      if (this._aws_youbora) {
        // @ts-expect-error TS2339
        this._aws_youbora.removeAdapter();
      }
    }
  };

  activateTHEOPlayer = ({
    cookie,
    vid,
    hasLLToggle,
    streamingType,
    useLLHLS,
  }: {
    cookie: Cookie;
    vid: string;
    hasLLToggle: boolean;
    streamingType: StreamingType;
    useLLHLS: boolean;
  }) => {
    if (this._player) {
      const videoElement = this.player.getHTMLVideoElement();
      videoElement.style.display = "block";
    }

    try {
      const videoURL = cookie.url;
      const policy = encodeURIComponent(cookie.cookie["CloudFront-Policy"]);
      const signature = encodeURIComponent(
        cookie.cookie["CloudFront-Signature"]
      );

      const parsedVideoURL = new URL(videoURL);

      const workerHost = appConfig.Cloudflare.vodWorkerUrl;

      const useCloudflareWorkers = this.props.isFanClub
        ? false
        : checkIfUseCloudFlare({ streamingType, videoId: vid });

      // Cloudflare Workers を利用する場合、URL を Workers の URL に差し替える (path以下は同じ)
      let newVideoURL = useCloudflareWorkers
        ? `${workerHost}/${parsedVideoURL.pathname.slice(
            1
          )}?policy=${policy}&signature=${signature}`
        : cookie.url;

      const dvrStartTime = this.getDvrStartTime(vid);

      if (streamingType === "Live" && dvrStartTime) {
        // medialiveにrequestを投げる時に、start parameterを付与する
        // ライブ配信の開始時より前の時間を適当に指定している
        // https://docs.aws.amazon.com/ja_jp/mediapackage/latest/ug/time-shifted.html
        // newVideoURL = newVideoURL + "?start=2025-12-08T13:05:00+09:00";
        // secondsの単位のunix timeで指定する
        newVideoURL = newVideoURL + `?start=${dvrStartTime}`;
      }

      /**
       * 二窓で再生できるようにcloudFront Signed URL をクエリパラメータとして付与するためのリクエストインターセプターを設定
       */
      try {
        // すでにリクエストインターセプターが存在する場合は削除する
        if (this._requestInterceptor) {
          this.player.network.removeRequestInterceptor(
            this._requestInterceptor
          );
          this._requestInterceptor = null;
        }

        if (streamingType === "Live" && this.props.isSignedUrlEnabled) {
          const {
            "CloudFront-Key-Pair-Id": keyPairId,
            "CloudFront-Policy": policy,
            "CloudFront-Signature": signature,
          } = cookie.cookie;

          this._requestInterceptor = (request) => {
            try {
              const url = new URL(request.url);
              url.searchParams.set("Policy", toUrlSafeForSignedUrl(policy));
              url.searchParams.set(
                "Signature",
                toUrlSafeForSignedUrl(signature)
              );
              url.searchParams.set("Key-Pair-Id", keyPairId);

              /**
               * 古いiPhoneだとsrcで付与したstartパラメータがリクエストに付与されないため、ここで付与する
               */
              if (
                !url.searchParams.has("start") &&
                dvrStartTime &&
                url.pathname.endsWith(".m3u8")
              ) {
                url.searchParams.set("start", String(dvrStartTime));
              }

              request.redirect(url.toString());
            } catch (e) {
              console.error("Failed to add signed URL query parameters:", e);
            }
          };

          this.player.network.addRequestInterceptor(this._requestInterceptor);
        }
      } catch (e) {
        console.error(
          "Failed to set up request interceptor for signed URLs:",
          e
        );
      }

      this.player.setHasLL(hasLLToggle);
      this.player.src({
        sources: [
          {
            src: newVideoURL,
            type: "application/x-mpegurl",
            liveOffset: cookie.hasOwnProperty("liveOffset")
              ? cookie.liveOffset
              : 10,
            crossOrigin: cookie.cors ? "use-credentials" : "",
            ...(useLLHLS
              ? {
                  lowLatency: true,
                  useManagedMediaSource: true,
                }
              : streamingType === "VOD" &&
                  this.props.isMMSForVODEnabled &&
                  this.canUseMMS()
                ? { useManagedMediaSource: true }
                : {}),
          },
        ],
      });

      if (useLLHLS) {
        if (this._onLLHLSError) {
          this.player.removeEventListener("error", this._onLLHLSError);
        }
        this._onLLHLSError = (error) => {
          try {
            if (this._onLLHLSError) {
              this.player.removeEventListener("error", this._onLLHLSError);
              this._onLLHLSError = null;
            }
          } catch (e) {
            console.error(
              "Error while removing LL-HLS error event listener:",
              e
            );
          }
          console.error("Error in THEOPlayer with LL-HLS:", error);
          this.cancelLLPlaying();
        };
        this.player.addEventListener("error", this._onLLHLSError);
      }

      // NPAW（youbora）へアナリティクスを送信する
      // - 本番環境では常に送信する
      // - 開発環境では `enableNpaw()` により明示的に有効化された場合のみ送信する
      if (!appConfig.logDebug || enableNpaw()) {
        this.player.src({
          ...this.player.getCurrentSrc(),
          analytics: [
            {
              integration: "youbora",
              // @ts-expect-error TS2345
              accountCode: appConfig.Youbora.accountCode,
              "user.name": firebase.auth()?.currentUser?.uid || "undefined", // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
              enableAnalytics: true,
              "content.title": vid,
              "content.duration":
                this.props.eventVideoMap?.[vid]?.duration || 0, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
              // ライブかアーカイブか
              "content.isLive": this._streamingType === "Live",
            },
          ],
        });

        /**
         * ABR設定
         * Live: bandwidth
         *   帯域幅に合わせる。画質よりもライブ配信を届けることを優先。
         * VOD: quality
         *   品質を重視する。アーカイブなので画質優先。
         *   NOTE: bandwidth にした方が、帯域によっては配信ページに入った瞬間に画質が良くなる
         * @see https://docs.theoplayer.com/api-reference/web/theoplayer.abrstrategytype.md
         */
        // TODO: video.jsでのABR設定を行う
        // this._player.abr.strategy =
        //   this._streamingType === "Live"
        //     ? this.props.liveSetting.abr.strategy
        //     : this.props.vodSetting.abr.strategy;
        /**
         * バッファ設定
         * Live: 6秒
         * VOD: 12秒
         * バッファが短いほどライブに近いが、帯域幅によって画質が落ちるため、Liveは小さく、VODは大きくしている
         * @see https://docs.theoplayer.com/api-reference/web/theoplayer.abrconfiguration.md
         */
        // TODO: video.jsでのbuffer設定を行う
        // this._player.abr.targetBuffer =
        //   this._streamingType === "Live"
        //     ? this.props.liveSetting.abr.targetBuffer
        //     : this.props.vodSetting.abr.targetBuffer;
      }

      /**
       * アーカイブ配信の場合、最後に見ていた再生時間にシークさせる
       */
      if (this._streamingType === "VOD") {
        const videoGroupsSet = this.props.getVideoGroupsSet(vid);
        if (videoGroupsSet) {
          const videoId = videoGroupsSet.syncPlaybackTimeOnSwitch
            ? videoGroupsSet.mainVideoId
            : vid;
          const previousPlayHeadPosition =
            loadCurrentTimeFromSessionStorage(videoId);
          if (previousPlayHeadPosition) {
            this.player.setCurrentTime(previousPlayHeadPosition);
          }
        }
      }
    } catch (e) {
      // Add error
      console.error(e);
      // @ts-expect-error TS2345
      errorReport(isError(e) && e);
      // npawのload errorなどの場合を考慮して、読み込み直している

      this.player.src({
        sources: [
          {
            src: cookie.url,
            type: "application/x-mpegurl",
            liveOffset: cookie.hasOwnProperty("liveOffset")
              ? cookie.liveOffset
              : 10,
            crossOrigin: cookie.cors ? "use-credentials" : "",
            ...(useLLHLS
              ? {
                  lowLatency: true,
                  useManagedMediaSource: true,
                }
              : streamingType === "VOD" &&
                  this.props.isMMSForVODEnabled &&
                  this.canUseMMS()
                ? { useManagedMediaSource: true }
                : {}),
          },
        ],
      });
    } finally {
      // `_is_initial_play` いらないけど残しておく
      if (this._is_initial_play) {
        // this._player.muted = false
        this._is_initial_play = false;
      }
      // FIX: ブラウザの仕様の為、リロードすると音声ON・自動再生がされない
      // https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
      // 現状、check_geo が重い処理の為 setTimeout と同じ動作をしている
      this.player.play();
    }
  };

  activateAWSPlayer = (cookie: Cookie, vid: string) => {
    const aws_video_elem = this._aws_player?.getHTMLVideoElement();
    this._aws_player?.addIVSPlayerEventListener(
      PlayerState.ENDED,
      // @ts-expect-error TS2345
      this.cancelLLPlaying
    );
    this._aws_player?.addIVSPlayerEventListener(
      PlayerEventType.ERROR,
      // @ts-expect-error TS2345
      this.cancelLLPlaying
    );

    if (aws_video_elem) {
      aws_video_elem.style.display = "block";
    }

    this._aws_player?.src({ src: cookie.url });
    this._aws_player?.autoPlay(true);
    this._aws_player?.play();

    if (this._aws_youbora == null) {
      // youbora.Log.logLevel = youbora.Log.Level.DEBUG
      this._aws_youbora = new youbora.Plugin({
        accountCode: appConfig.Youbora.accountCode,
      });
    }
    try {
      this._aws_player?.registerNPAWPlugin({
        vid,
        isLive: this._streamingType === "Live",
        duration: undefined,
      });
    } catch (e) {
      console.error(e);
      // @ts-expect-error TS2345
      errorReport(isError(e) && e);
    }
  };

  exitFullscreenTHEOPlayer = () => {
    try {
      if (this._player && this.player.isFullscreen()) {
        this.player.exitFullscreen();
      }
    } catch (e) {
      console.error(e);
      errorReport(
        isError(e) ? e : new Error("Unknown error in exitFullscreenTHEOPlayer")
      );
    }
  };

  exitFullscreenAWSPlayer = () => {
    try {
      if (this._aws_player && this._aws_player.isFullscreen()) {
        this._aws_player.exitFullscreen();
      }
    } catch (e) {
      console.error(e);
      errorReport(
        isError(e) ? e : new Error("Unknown error in exitFullscreenAWSPlayer")
      );
    }
  };

  updatePlayer = ({
    cookie,
    vid,
    streamingType,
    hasLLToggle = false,
    useIVS = false,
    useLLHLS = false,
  }: {
    cookie: Cookie;
    vid: string;
    streamingType: StreamingType;
    hasLLToggle?: boolean;
    useIVS?: boolean;
    useLLHLS?: boolean;
  }) => {
    // @ts-expect-error TS2345
    set_cookie(cookie, domain_from_url(cookie.url));
    if (useIVS && !!this._aws_player) {
      // 低遅延有効
      this.hideTHEOPlayer();
      this.activateAWSPlayer(cookie, vid);

      if (isSmartPhone()) {
        // ネイティブフルスクリーン状態で低遅延を切り替えると、hideされたプレイヤーが
        // フルスクリーン状態で残り続けてしまい表示が崩れるため、フルスクリーンを抜ける
        // ネイティブフルスクリーンを使用するのは isSmartPhone() が true の場合のみ
        this.exitFullscreenTHEOPlayer();
        // FIXME: 再度フルスクリーンにしたいが、表示が崩れてしまうため、requestFullscreenはコメントアウトしている
        // this._aws_player.requestFullscreen();
      }
    } else {
      // 低遅延無効
      try {
        this.hideAWSPlayer();
      } catch (e) {
        console.error(e);
        // @ts-expect-error TS2345
        errorReport(isError(e) && e);
      }
      try {
        this.activateTHEOPlayer({
          cookie,
          vid,
          hasLLToggle,
          streamingType,
          useLLHLS,
        });
      } catch (e) {
        console.error(e);
        // @ts-expect-error TS2345
        errorReport(isError(e) && e);
      }
      if (isSmartPhone()) {
        // ネイティブフルスクリーン状態で低遅延を切り替えると、hideされたプレイヤーが
        // フルスクリーン状態で残り続けてしまい表示が崩れるため、フルスクリーンを抜ける
        // ネイティブフルスクリーンを使用するのは isSmartPhone() が true の場合のみ
        this.exitFullscreenAWSPlayer();
        // FIXME: 再度フルスクリーンにしたいが、表示が崩れてしまうため、requestFullscreenはコメントアウトしている
        // this.player.requestFullscreen();
      }
    }
    this.setState({ hasURLUpdatingRequested: false });
  };

  setTHEOPlayerElementHeight = (THEOPlayerElementHeight: number) => {
    this.setState({ THEOPlayerElementHeight });
  };

  setVideoRef = (el: HTMLVideoElement | null) => {
    this._videoRef = el;
    const parent = el?.closest(".video-js");
    if (parent) {
      if (this._hoverObserver) {
        this._hoverObserver.disconnect();
      }
      this._hoverObserver = new MutationObserver(() => {
        const aside = document.querySelector(
          ".streaming_aside_layout"
        ) as HTMLElement;
        const isPaused = parent.classList.contains("vjs-paused");
        const isUserActive = parent.classList.contains("vjs-user-active");
        const isControllerDisplaying = isPaused || isUserActive;
        if (this.state.isControllerDisplaying !== isControllerDisplaying) {
          this.setState({ isControllerDisplaying });

          if (screenfull.isFullscreen && aside) {
            aside.style.height = isControllerDisplaying
              ? "calc(100vw / (16 / 9) - 76px)"
              : "calc(100vw / (16 / 9))";
          } else {
            aside.style.height = "";
          }
        }
      });
      this._hoverObserver.observe(parent, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  };

  render() {
    if (!this.props.event || !this.props.event._id) {
      return null;
    }

    /**
     * constructorで設定でsetCurrentVideoIdが呼ばれて
     * reduxのcurrentVideoId値が初期化されるまでは、this.props.vidでfallback
     */
    const currentVideoId = this.props.currentVideoId ?? this.props.vid;

    const isDisplayActionArea = Boolean(
      (this._streamingType === "Live" && this._player) ||
      (this._streamingType === "VOD" && this._player && this.state.eventStart)
    );

    const videoGroupsSet = this.props.getVideoGroupsSet(currentVideoId);
    const mainVideoId = videoGroupsSet?.mainVideoId;

    return (
      <React.Fragment>
        <PCView>
          <AlertBar />
        </PCView>

        <StreamingFunctionComponent
          eventStart={this.state.eventStart}
          player={this._player}
          awsPlayer={this._aws_player}
          useLL={this.state.use_ll}
          hasURL={this.state.hasURL}
          fullscreenElementRef={this._fullscreenElementRef}
          streamingType={this._streamingType}
          currentVideoId={currentVideoId}
          isDisplayActionArea={isDisplayActionArea}
          isControllerRendering={this.state.isControllerRendering}
          isCancelLowLatencyMessage={this.state.isCancelLowLatencyMessage}
          THEOPlayerElementHeight={this.state.THEOPlayerElementHeight}
          dvrStartTime={this.getDvrStartTime(currentVideoId)}
          setVideoRef={this.setVideoRef}
          setIVSVideoRef={(el: HTMLVideoElement | null) => {
            this._IVSVideoRef = el;
          }}
          setLLUrl={this.setLL}
          setWatchVideo={this.setWatchVideo}
          setIsControllerRendering={() =>
            this.setState({ isControllerRendering: true })
          }
          setTHEOPlayerElementHeight={this.setTHEOPlayerElementHeight}
          mainVideoId={mainVideoId}
          isPollingEnabled={this.props.isPollingEnabled}
        />

        <PawCharge />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: Store) => {
  const props: ValueProps = {
    pathname: state.router.location.pathname,
    giftItemMap: state.streaming.giftItemMap,
    pawBalance: state.purchase.emoBalance.paidValue,
    isDarkModeEnabled: state.streaming.streamingSettings.isDarkModeEnabled,
    isOnlyChatEnabled: state.streaming.streamingSettings.isOnlyChatEnabled,
    isOpenError: state.modal.isOpenError,
    currentVideoId: state.streaming.currentVideoId,
  };
  return props;
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  const func: FuncProps = {
    getEmoBalance: () => {
      dispatch(purchaseActions.getEmoBalance.started());
    },
    fetchGiftItems: (payload: ReqFetchGiftItems) => {
      dispatch(streamingActions.fetchGiftItems.started(payload));
    },
    usePAW: (payload) => {
      dispatch(purchaseActions.usePAW.started(payload));
    },
    toggleError: (msgObj) => {
      dispatch(modalActions.toggleError(msgObj));
    },
    setStateByKey: (payload: ReqSetStateByKey) => {
      dispatch(streamingActions.setStateByKey(payload));
    },
    fetchCommentShardId: (payload: ReqFetchShardIdArgs) => {
      dispatch(streamingActions.fetchCommentShardId.started(payload));
    },
    setCurrentVideoId: (videoId: string) => {
      dispatch(streamingActions.setCurrentVideoId(videoId));
    },
  };
  return func;
};

const TransStreaming = withTranslation()(Streaming);
export default connect(mapStateToProps, mapDispatchToProps)(TransStreaming);

const StreamingFunctionComponent: FC<{
  eventStart: Timestamp;
  player: PlainVideoPlayer | null;
  awsPlayer: IVSVideoPlayer | null;
  useLL: boolean;
  hasURL: boolean;
  fullscreenElementRef: React.RefObject<HTMLDivElement>;
  streamingType: StreamingType;
  currentVideoId: string;
  isDisplayActionArea: boolean;
  isControllerRendering: boolean;
  isCancelLowLatencyMessage: boolean;
  THEOPlayerElementHeight: number;
  dvrStartTime?: number;
  mainVideoId: string | undefined;
  isPollingEnabled: boolean;
  setVideoRef: (videoRef: HTMLVideoElement | null) => void;
  setIVSVideoRef: (videoRef: HTMLVideoElement | null) => void;
  setLLUrl: (check: boolean) => void;
  setWatchVideo: ({ vid, prevVid }: { vid: string; prevVid?: string }) => void;
  setIsControllerRendering: () => void;
  setTHEOPlayerElementHeight: (THEOPlayerElementHeight: number) => void;
}> = ({
  eventStart,
  player,
  awsPlayer,
  useLL,
  hasURL,
  fullscreenElementRef,
  streamingType,
  currentVideoId,
  isDisplayActionArea,
  isControllerRendering,
  isCancelLowLatencyMessage,
  THEOPlayerElementHeight,
  dvrStartTime,
  mainVideoId,
  isPollingEnabled,
  setVideoRef,
  setIVSVideoRef,
  setLLUrl,
  setWatchVideo,
  setIsControllerRendering,
  setTHEOPlayerElementHeight,
}) => {
  const { isTheaterModeEnabled, disabledTheaterMode } = useTheaterMode();

  const dispatch = useDispatch();

  const eventInfo = useSelector((state: Store) => state.event.displayEvent);
  const eventVideoMap = useSelector(
    (state: Store) => state.streaming.eventVideoMap
  );
  useEffect(() => {
    dispatch(
      ticketActions.fetchEventGoods.started({ eventId: eventInfo.eventId })
    );
  }, [dispatch, eventInfo.eventId]);
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const { actionAreaType, handleChangeActionAreaType } = useActionAreaType();
  const { selectedCommentFilter, handleChangeCommentFilter } =
    useCommentFilter();

  /**
   * @description
   * ライブコマース機能が有効かどうかを判定する
   * 旧実装に合わせて、streamingInfoとeventVideoMap両方で判定を行う
   * @see https://github.com/balus-co-ltd/spwn/blob/f0b23a268b98285e13cea4b870d201b29470f8f6/legacy/packages/portal/src/components/streaming/StreamingCommentTab.tsx#L34-L37
   */
  const currentVideo = eventVideoMap?.[currentVideoId];
  const isLiveCommerceEnabled =
    (eventInfo.streamingInfo?.commentTab.isLiveCommerceEnabled ?? false) &&
    (currentVideo?.isLiveCommerceEnabled === undefined ||
      currentVideo.isLiveCommerceEnabled);

  useEffect(() => {
    // コメントページを閉じた時に、コメントを再表示する
    const channel = new BroadcastChannel("popout-closed");
    channel.onmessage = () => {
      handleChangeCommentFilter("all");
    };
    return () => {
      channel.close();
    };
  }, [handleChangeCommentFilter]);

  useEffect(() => {
    const onCloseWindow = () => {
      if (!isPopOut()) return;

      // コメントページでのみ、ウィンドウを閉じたことを伝える
      const channel = new BroadcastChannel("popout-closed");
      // MEMO: メッセージの内容は特に必要ないので null を送信
      channel.postMessage(null);
      channel.close();
    };

    window.addEventListener("beforeunload", onCloseWindow);
    return () => {
      window.removeEventListener("beforeunload", onCloseWindow);
    };
  }, []);

  const {
    isOpenLiveEvent,
    isLiveEventEnabled,
    handleCloseLiveEvent,
    handleToggleLiveEvent,
  } = useLiveEvent(currentVideo);

  const {
    isOpen: isOpenAnnouncement,
    isEditing: isEditingAnnouncement,
    streamingAnnouncement,
    handleCloseAnnouncement,
    handleToggleAnnouncement,
    setIsEditing: setIsEditingAnnouncement,
    setStreamingAnnouncement,
    onStreamingAnnouncementLocalDeleted,
  } = useAnnouncement(mainVideoId);

  const handleToggleAnnouncementWithCloseLiveEvent = useCallback(() => {
    handleCloseLiveEvent();
    handleToggleAnnouncement();
  }, [handleToggleAnnouncement, handleCloseLiveEvent]);

  const handleToggleLiveEventWithCloseAnnouncement = useCallback(() => {
    handleCloseAnnouncement();
    handleToggleLiveEvent();
  }, [handleToggleLiveEvent, handleCloseAnnouncement]);

  const { isFullscreen } = useFullscreen();

  const isChatAdmin = useIsChatAdmin(eventInfo.eventId);

  const streamingActionAreaV2 =
    mainVideoId !== undefined ? (
      <StreamingActionAreaV2
        event={eventInfo}
        player={player}
        eventStart={eventStart}
        mainVideoId={mainVideoId}
        THEOPlayerElementHeight={THEOPlayerElementHeight}
        isChatAdmin={isChatAdmin}
        isHiddenStreamingComment={eventInfo.isHiddenStreamingComment ?? false}
        isPollingEnabled={isPollingEnabled}
        isLiveCommerceEnabled={isLiveCommerceEnabled}
        isOpenAnnouncement={isOpenAnnouncement}
        handleToggleAnnouncement={handleToggleAnnouncementWithCloseLiveEvent}
        onStreamingAnnouncementLocalDeleted={
          onStreamingAnnouncementLocalDeleted
        }
        isEditingAnnouncement={isEditingAnnouncement}
        setIsEditingAnnouncement={setIsEditingAnnouncement}
        streamingAnnouncement={streamingAnnouncement}
        setStreamingAnnouncement={setStreamingAnnouncement}
        isOpenLiveEvent={isOpenLiveEvent}
        handleToggleLiveEvent={handleToggleLiveEventWithCloseAnnouncement}
        actionAreaType={actionAreaType}
        selectedCommentFilter={selectedCommentFilter}
        isLiveEventEnabled={isLiveEventEnabled}
        handleChangeCommentFilter={handleChangeCommentFilter}
        handleCloseActionArea={() => {
          handleChangeActionAreaType("comment");
        }}
        hasDvrStartTime={dvrStartTime !== undefined}
      />
    ) : null;

  if (isPopOut()) {
    return streamingActionAreaV2;
  }

  return (
    <Box
      component={"section"}
      id={"Streaming"}
      css={css`
        /*
       * @description プレイヤーのサイズ調整に関するCSSの説明
       * .streaming_main_layout {
       *  プレイヤーの最小横幅を「高さ230pxの16:9比率」に合わせて計算している。230px × 16 ÷ 9 ≒ 409px となり、縦横比が崩れないよう最低限の横幅を確保している
       *  min-width: calc(230px * (16 / 9));
       *
       *  画面の高さ（100dvh）からヘッダー等と画面したの余白を引いた残りの高さを使い、そこから16:9比率で最大横幅を算出している。これにより、ウィンドウサイズに応じてプレイヤーの横幅が大きくなりすぎないように調整している
       *  max-width: calc((100dvh - 66px - 50px) * (16 / 9));
       */
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
        max-width: 1800px;
        margin: 0 auto;
        background-color: ${isDarkModeEnabled ? "#000" : "#fff"};
        transition: 0.3s;
        @media screen and (min-width: 768px) {
          flex-direction: row;
          min-height: calc(100vh - 64px); /* fallback */
          @supports (min-height: 100dvh) {
            min-height: calc(100dvh - 64px);
          }
        }
        .streaming_main_layout {
          width: 100%;
          min-height: 100vh; /* fallback */
          @supports (min-height: 100dvh) {
            min-height: 100dvh;
          }
          @media screen and (min-width: 768px) {
            position: relative;
            min-width: calc(230px * (16 / 9));
            max-width: calc((100vh - 66px - 50px) * (16 / 9)); /* fallback */
            @supports (max-width: 100dvh) {
              max-width: calc((100dvh - 66px - 50px) * (16 / 9));
            }
            min-height: auto;
          }
        }
        .streaming_aside_layout {
          position: relative;
          width: 100%;
          opacity: 1;
          visibility: visible;
          transition: 0.3s;
          @media screen and (min-width: 768px) {
            width: 360px;
            min-width: 360px;
            min-height: auto;
          }
          .streaming_action_area {
            &.streaming_action_area_height {
              height: calc(100vh - (100vw / (16 / 9))); /* fallback */
              @supports (min-height: 100dvh) {
                height: calc(100dvh - (100dvw / (16 / 9)));
              }
              @media screen and (min-width: 768px) {
                height: ${THEOPlayerElementHeight + 12}px;
                min-height: 600px;
                max-height: calc(100vh - 65px); /* fallback */
                @supports (min-height: 100dvh) {
                  max-height: calc(100dvh - 65px);
                }
              }
            }
            &.streaming_action_area_events {
              height: auto;
              @media screen and (min-width: 768px) {
                height: calc(100vh - 65px); /* fallback */
                @supports (min-height: 100dvh) {
                  height: calc(100dvh - 65px);
                }
              }
            }
          }
        }
        &.${fullscreenClassName} {
          position: fixed;
          top: 50%;
          left: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: none;
          background-color: transparent;
          transform: translateY(-50%);
          .streaming_main_layout {
            width: 100%;
          }
          .streaming_aside_layout {
            position: fixed;
            top: 0;
            left: 0;
            width: 360px;
            height: calc(100vw / (16 / 9) - 76px);
            transition: 0.5s;
            z-index: 1010;
          }
          .streaming_action_area {
            height: 100%;
          }
        }
      `}
    >
      <Box className={"streaming_main_layout"}>
        <StreamingFullscreen
          fullscreenRef={fullscreenElementRef}
          player={player}
          streamingType={streamingType}
          eventInfo={eventInfo}
          eventStart={eventStart}
          mainVideoId={mainVideoId}
          isDisplayActionArea={isDisplayActionArea}
          isOpenAnnouncement={isOpenAnnouncement}
          streamingAnnouncement={streamingAnnouncement}
          handleCloseAnnouncement={handleCloseAnnouncement}
          isFullscreen={isFullscreen}
          hasDvrStartTime={dvrStartTime !== undefined}
          isPollingEnabled={isPollingEnabled}
        >
          <StreamingScreenV2
            setTHEOPlayerElementHeight={setTHEOPlayerElementHeight}
            THEOPlayerElementHeight={THEOPlayerElementHeight}
            player={player}
            awsPlayer={awsPlayer}
            eventInfo={eventInfo}
            useLL={useLL}
            hasURL={hasURL}
            setLLUrl={setLLUrl}
            isControllerRendering={isControllerRendering}
            setIsControllerRendering={setIsControllerRendering}
            isCancelLowLatencyMessage={isCancelLowLatencyMessage}
            setVideoRef={setVideoRef}
            setIVSVideoRef={setIVSVideoRef}
          />
        </StreamingFullscreen>

        {mainVideoId && (
          <StreamingBottomArea
            eventInfo={eventInfo}
            player={player}
            streamingType={streamingType}
            mainVideoId={mainVideoId}
            currentVideoId={currentVideoId}
            setWatchVideo={setWatchVideo}
            actionAreaType={actionAreaType}
            isOpenAnnouncement={isOpenAnnouncement}
            eventStart={eventStart}
            THEOPlayerElementHeight={THEOPlayerElementHeight}
            selectedCommentFilter={selectedCommentFilter}
            hasDvrStartTime={dvrStartTime !== undefined}
            isChatAdmin={isChatAdmin}
            isEditingAnnouncement={isEditingAnnouncement}
            isLiveEventEnabled={isLiveEventEnabled}
            setIsEditingAnnouncement={setIsEditingAnnouncement}
            streamingAnnouncement={streamingAnnouncement}
            isOpenLiveEvent={isOpenLiveEvent}
            handleToggleLiveEvent={handleToggleLiveEventWithCloseAnnouncement}
            isLiveCommerceEnabled={isLiveCommerceEnabled}
            setStreamingAnnouncement={setStreamingAnnouncement}
            handleChangeCommentFilter={handleChangeCommentFilter}
            onStreamingAnnouncementLocalDeleted={
              onStreamingAnnouncementLocalDeleted
            }
            handleToggleAnnouncement={
              handleToggleAnnouncementWithCloseLiveEvent
            }
            handleClickCommentNavigation={() => {
              handleChangeActionAreaType("comment");
              if (selectedCommentFilter === "none") {
                handleChangeCommentFilter("all");
              }
              disabledTheaterMode();
              scrollTop();
            }}
            isPollingEnabled={isPollingEnabled}
            handleClickGoodsNavigation={() => {
              handleChangeActionAreaType("goods");
              disabledTheaterMode();
              scrollTop();
            }}
          />
        )}
      </Box>

      <PCView>
        <Box
          className={"streaming_aside_layout"}
          style={{
            ...(isTheaterModeEnabled && {
              width: "0px",
              minWidth: "0px",
              opacity: 0,
              visibility: "hidden",
              overflow: "hidden",
            }),
          }}
        >
          {/* ユーザー操作エリア (コメント、ギフト、etc(later: goods, quiz)) */}
          {isDisplayActionArea && streamingActionAreaV2}
        </Box>
      </PCView>
    </Box>
  );
};
